import type { ComponentType } from "react";
import {
  FileText,
  Plus,
  Receipt,
  Wrench,
  CheckCircle2,
  MinusCircle,
} from "lucide-react";
import { cn, formatDate } from "@/lib/utils";
import type {
  LifecycleEvent,
  LifecycleEventType,
} from "@/lib/lifecycle-events";

// event_type → icon + marker tint. "faded, not flagged" (ADR-0019): a lost
// close is muted, never danger; a won close is success; every in-flight
// milestone is accent. Color lives only on the marker (the chrome stays
// monochrome).
type Tint = "accent" | "success" | "muted";

const DISPATCH: Record<
  LifecycleEventType,
  { icon: ComponentType<{ className?: string }>; tint: Tint }
> = {
  created: { icon: Plus, tint: "accent" },
  proposal: { icon: FileText, tint: "accent" },
  job: { icon: Wrench, tint: "accent" },
  invoice: { icon: Receipt, tint: "accent" },
  won: { icon: CheckCircle2, tint: "success" },
  lost: { icon: MinusCircle, tint: "muted" },
};

const MARKER_TINT: Record<Tint, string> = {
  accent: "border-[color:var(--color-accent)] text-[color:var(--color-accent)]",
  success:
    "border-[color:var(--color-success)] text-[color:var(--color-success)]",
  muted: "border-[color:var(--color-border)] text-[color:var(--color-text-lo)]",
};

// Active-marker fill: when a milestone is the scroll-spy's active stage the
// marker INVERTS — accent background, surface-colored glyph — instead of the
// resting outlined treatment. This is one of three redundant, non-color active
// signals (filled marker + halo ring + bolder title) so the active stage reads
// in greyscale / for CVD users (ADR-0019 — color-as-only-signal is forbidden).
const MARKER_ACTIVE: Record<Tint, string> = {
  accent:
    "border-[color:var(--color-accent)] bg-[color:var(--color-accent)] text-[color:var(--color-surface)]",
  success:
    "border-[color:var(--color-success)] bg-[color:var(--color-success)] text-[color:var(--color-surface)]",
  muted:
    "border-[color:var(--color-text-lo)] bg-[color:var(--color-text-lo)] text-[color:var(--color-surface)]",
};

/**
 * Opportunity lifecycle timeline — the timestamped "moment" axis (decision T2),
 * rendered oldest-first (earliest → latest) so the rail reads as a chronological
 * story top-to-bottom and stays directionally aligned with the activity feed
 * beside it. A vertical rail of icon markers connected by a hairline, each with
 * a title, an absolute date (§4.3), and an optional detail line. Props-driven
 * over `LifecycleEvent[]` so slice 2 can swap the client deriver for a server
 * endpoint with zero changes here. Renders a Type-2 teach line when the
 * opportunity has no dated milestones yet.
 *
 * ADV-1345: when `onSelect` is supplied each milestone becomes a focusable,
 * keyboard-activatable control wired to the lifecycle scroll-spy — clicking jumps
 * the activity feed to that milestone's moment, and the spy reports back which
 * milestone the scroll viewport is in via `activeId`. The active milestone is
 * marked `aria-current` and rendered with three redundant non-color signals
 * (filled marker, halo ring, bolder title). When `onSelect` is omitted the
 * rail renders its original non-interactive markup (used in read-only mounts).
 */
export function OpportunityLifecycleTimeline({
  events,
  activeId,
  onSelect,
}: {
  events: LifecycleEvent[];
  /** Id of the milestone the scroll viewport is currently in (scroll-spy). */
  activeId?: string | null;
  /** Click/keyboard handler — when set, milestones become focusable buttons. */
  onSelect?: (event: LifecycleEvent) => void;
}) {
  if (events.length === 0) {
    return (
      <p className="py-6 typo-label normal-case tracking-normal text-[color:var(--color-text-lo)]">
        Lifecycle milestones appear here as this opportunity progresses —
        proposals, jobs, and invoices land on the timeline as they happen.
      </p>
    );
  }

  // Deriver already returns oldest-first; render it as-is so the rail reads
  // earliest → latest (chronological story), matching the activity feed.
  return (
    <ol className="relative">
      {events.map((event, i) => {
        const { icon: Icon, tint } = DISPATCH[event.type];
        const last = i === events.length - 1;
        const interactive = typeof onSelect === "function";
        const active = interactive && event.id === activeId;

        const marker = (
          <span
            className={cn(
              "z-10 flex size-6 shrink-0 items-center justify-center rounded-full border bg-[color:var(--color-surface)] transition-colors",
              active ? MARKER_ACTIVE[tint] : MARKER_TINT[tint],
              // Halo ring is a non-color (shape) active signal that survives
              // greyscale — it does not reflow the marker column.
              active &&
                "ring-2 ring-offset-2 ring-[color:var(--color-accent)] ring-offset-[color:var(--color-surface)]",
            )}
          >
            <Icon className="size-3.5" />
          </span>
        );

        const body = (
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <span
                className={cn(
                  "typo-body text-[color:var(--color-text-hi)]",
                  // Bolder title is the second non-color active signal.
                  active && "font-semibold",
                )}
              >
                {event.title}
              </span>
              <time className="shrink-0 typo-mono text-xs tabular-nums text-[color:var(--color-text-lo)]">
                {formatDate(event.timestamp)}
              </time>
            </div>
            {event.detail && (
              <span className="block truncate typo-label normal-case tracking-normal text-[color:var(--color-text-md)]">
                {event.detail}
              </span>
            )}
          </div>
        );

        return (
          <li key={event.id} className="relative flex gap-3 pb-5 last:pb-0">
            {/* Connecting hairline down to the next marker. */}
            {!last && (
              <span
                aria-hidden
                className="absolute left-3 top-6 h-full w-px -translate-x-1/2 bg-[color:var(--color-border)]"
              />
            )}
            {interactive ? (
              <button
                type="button"
                onClick={() => onSelect(event)}
                aria-current={active ? "true" : undefined}
                className={cn(
                  "group flex w-full gap-3 rounded-md text-left transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)]",
                )}
              >
                {marker}
                {body}
              </button>
            ) : (
              <>
                {marker}
                {body}
              </>
            )}
          </li>
        );
      })}
    </ol>
  );
}
